import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { isHybridMode } from '../../utils/config';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import TeamLogo from './TeamLogo';
import './Leagues.css';

// Date-format options live at module level; the formatter is built per render
// with currentLocale() so it reacts to a language change (a module-level
// Intl.DateTimeFormat would freeze the locale at import time).
const DAY_FMT_OPTS = { weekday: 'long', day: 'numeric', month: 'long' };
const TIME_FMT_OPTS = { hour: '2-digit', minute: '2-digit' };

/** Local calendar-day key (YYYY-MM-DD) used to group fixtures by day. */
function dayKey(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Pre-fill value for an <input type="datetime-local"> from an ISO string (local time). */
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Season schedule: upcoming fixtures grouped into an agenda by calendar day
 * (with an "Ej tidsbestämda" bucket for unbooked ones) and a "Spelade" list for
 * recorded results. Logged-in participants and admins can soft-book a kickoff
 * time (a non-binding plan — the match stays launchable any time) and start
 * their own fixtures; the API authorises participants and admins, so a
 * non-participant simply gets an error. The home team is marked because it bans
 * (BO1) / picks (BO3+) first; the return meeting flips the sides.
 *
 * @param {object} props - Component props.
 * @param {string} props.seasonId - The season whose fixtures to show.
 */
export default function SeasonFixtures({ seasonId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!seasonId) {
      setFixtures([]);
      return;
    }
    // Forward the JWT so the API can flag each fixture ``mine`` (your own
    // fixtures are the only ones a participant may launch/book/reset).
    fetch(`${API_URL}/api/leagues/seasons/${seasonId}/matches`, {
      headers: isLoggedIn ? getAuthHeaders() : {},
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setFixtures(Array.isArray(d) ? d : []))
      .catch(() => setFixtures([]));
  }, [seasonId, isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const launch = async (fixture) => {
    setBusyId(fixture.id);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/leagues/matches/${fixture.id}/launch`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        setError(data?.error || data?.detail || t('leagues.launchFailed'));
        return;
      }
      navigate(`/lobby/${data.match_id}`);
    } finally {
      setBusyId(null);
    }
  };

  const reset = async (fixture) => {
    if (!window.confirm(t('leagues.resetConfirm'))) return;
    setBusyId(fixture.id);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/leagues/matches/${fixture.id}/reset`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: '{}',
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        setError(data?.error || data?.detail || t('leagues.resetFailed'));
        return;
      }
      load();
    } finally {
      setBusyId(null);
    }
  };

  // Soft-book (or clear, when value is null) a fixture's kickoff time.
  const saveSchedule = async (fixture, value) => {
    setBusyId(fixture.id);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/leagues/matches/${fixture.id}/schedule`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_at: value ? new Date(value).toISOString() : null }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        setError(data?.error || data?.detail || t('leagues.scheduleSaveFailed'));
        return;
      }
      setEditingId(null);
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (fixtures.length === 0) return null;

  const dayFmt = new Intl.DateTimeFormat(currentLocale(), DAY_FMT_OPTS);
  const timeFmt = new Intl.DateTimeFormat(currentLocale(), TIME_FMT_OPTS);

  const canLaunch = isHybridMode && isLoggedIn;

  // Upcoming = not yet recorded; played = recorded result.
  const upcoming = fixtures.filter((f) => !f.points_awarded);
  const played = fixtures.filter((f) => f.points_awarded);

  // Group booked upcoming fixtures by day (ascending); unbooked go in a bucket.
  const dated = upcoming
    .filter((f) => f.scheduled_at)
    .slice()
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const undated = upcoming.filter((f) => !f.scheduled_at);
  const groups = [];
  let cursor = null;
  dated.forEach((f) => {
    const d = new Date(f.scheduled_at);
    const key = dayKey(d);
    if (!cursor || cursor.key !== key) {
      cursor = { key, label: dayFmt.format(d), items: [] };
      groups.push(cursor);
    }
    cursor.items.push(f);
  });

  const renderRow = (f, { showTime = false } = {}) => {
    const recorded = f.points_awarded;
    const live = Boolean(f.match_id) && !recorded;
    // Only the fixture's own participants (or an admin) may act — the API flags
    // these with ``mine``; everyone else sees the schedule read-only.
    const mine = Boolean(f.mine);
    const startable = canLaunch && mine && f.status === 'scheduled' && !f.match_id;
    const resettable = canLaunch && mine && !recorded && f.status !== 'scheduled';
    const editable = canLaunch && mine && !recorded;
    const editing = editingId === f.id;
    const hasActions = startable || (editable && !editing) || resettable;
    return (
      <div key={f.id} className="fixture-row">
        <div className="fixture-teams">
          <span className="fixture-home-flag" title={t('leagues.homeFlagTitle')}>🏠</span>
          <span className="fixture-team">
            <TeamLogo logoUrl={f.home_logo_url} name={f.home_name} />
            {f.home_name}
          </span>
          <span className="fixture-vs">
            {recorded ? `${f.home_score}–${f.away_score}` : 'vs'}
          </span>
          <span className="fixture-team">
            <TeamLogo logoUrl={f.away_logo_url} name={f.away_name} />
            {f.away_name}
          </span>
        </div>
        <div className="fixture-meta">
          <span className={`league-season-pill ${
            recorded ? 'is-finished' : live ? 'is-active' : 'is-upcoming'
          }`}>
            {f.status_display || f.status}
          </span>
          {f.division_name && <span className="fixture-div">{f.division_name}</span>}
          {showTime && f.scheduled_at && (
            <span className="fixture-time">🕑 {timeFmt.format(new Date(f.scheduled_at))}</span>
          )}
          {live && f.match_id && (
            <button
              className="btn-subtle"
              onClick={() => navigate(`/stats/matches/${f.match_id}`)}
            >
              {t('leagues.viewMatch')}
            </button>
          )}
        </div>
        {hasActions && (
          <div className="fixture-actions">
            {startable && (
              <button
                className="btn-primary-sm"
                disabled={busyId === f.id}
                onClick={() => launch(f)}
              >
                {busyId === f.id ? t('live.starting') : t('leagues.startMatch')}
              </button>
            )}
            {editable && !editing && (
              <button
                className="btn-subtle"
                disabled={busyId === f.id}
                onClick={() => {
                  setEditingId(f.id);
                  setEditValue(toLocalInput(f.scheduled_at));
                }}
              >
                {f.scheduled_at ? t('leagues.changeTime') : t('leagues.bookTime')}
              </button>
            )}
            {resettable && (
              <button
                className="btn-subtle fixture-delete"
                disabled={busyId === f.id}
                onClick={() => reset(f)}
              >
                {t('leagues.reset')}
              </button>
            )}
          </div>
        )}
        {editing && (
          <div className="fixture-booking">
            <input
              type="datetime-local"
              className="fixture-booking-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
            />
            <button
              className="btn-primary-sm"
              disabled={busyId === f.id || !editValue}
              onClick={() => saveSchedule(f, editValue)}
            >
              {t('settings.save')}
            </button>
            {f.scheduled_at && (
              <button
                className="btn-subtle"
                disabled={busyId === f.id}
                onClick={() => saveSchedule(f, null)}
              >
                {t('queue.clear')}
              </button>
            )}
            <button className="btn-subtle" onClick={() => setEditingId(null)}>
              {t('matchDetail.cancel')}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="season-fixtures">
      <h2 className="league-section-title">{t('leagues.scheduleTitle')}</h2>
      {error && <div className="league-form-error">{error}</div>}

      {(groups.length > 0 || undated.length > 0) && (
        <div className="fixture-agenda">
          {groups.map((g) => (
            <div key={g.key} className="fixture-day">
              <h3 className="fixture-day-label">{g.label}</h3>
              <div className="fixture-list">
                {g.items.map((f) => renderRow(f, { showTime: true }))}
              </div>
            </div>
          ))}
          {undated.length > 0 && (
            <div className="fixture-day">
              <h3 className="fixture-day-label is-undated">{t('leagues.undatedBucket')}</h3>
              <div className="fixture-list">
                {undated.map((f) => renderRow(f))}
              </div>
            </div>
          )}
        </div>
      )}

      {played.length > 0 && (
        <div className="fixture-played">
          <h3 className="fixture-day-label">{t('leagues.played')}</h3>
          <div className="fixture-list">
            {played.map((f) => renderRow(f))}
          </div>
        </div>
      )}

      <p className="fixture-legend">
        {t('leagues.fixtureLegend')}
      </p>
    </div>
  );
}
